import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Toggle from "components/form/toggle/Toggle";
import { cloneDeep } from "lodash";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { websiteBuilderUpdateContainerStretch, websiteBuilderUpdateElementStretch } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { WebsiteBuilderRange } from "pages/secure/facility/websiteBuilder/editor/components/range/WebsiteBuilderRange";
import {
  useWebsiteBuilderDebouncedUpdate,
  WebsiteBuilderDebouncedUpdateTime,
} from "pages/secure/facility/websiteBuilder/editor/hooks/debouncedUpdate/useWebsiteBuilderDebouncedUpdate";
import { IWebsiteBuilderSectionEntityStretch, TWebsiteBuilderSectionEntity, WEBSITE_BUILDER_SECTION_ENTITY_STRETCH_MAX_MARGIN, WEBSITE_BUILDER_SECTION_ENTITY_STRETCH_MIN_MARGIN } from "pages/secure/facility/websiteBuilder/editor/nodes/websiteBuilderSectionEntity";
import { WebsiteBuilderPanelContent } from "pages/secure/facility/websiteBuilder/editor/shell/panel/content/WebsiteBuilderPanelContent";

interface ISectionEntityViewStretchSettingsProps {
  node: TWebsiteBuilderSectionEntity;
  stretch: IWebsiteBuilderSectionEntityStretch;
  closeSettings: () => void;
}

export function SectionEntityViewStretchSettings(props: ISectionEntityViewStretchSettingsProps) {
  const { node, stretch, closeSettings } = props;
  
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const activePageId = useAppSelector(store => store.websiteBuilderStore.editor.activePageId);
  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);

  const [updatedStretch, setUpdatedStretch] = useState<IWebsiteBuilderSectionEntityStretch>(cloneDeep(stretch));

  function dispatchStretch(prevStretch: IWebsiteBuilderSectionEntityStretch, nextStretch: IWebsiteBuilderSectionEntityStretch) {
    if (node.type === "element") {
      dispatch(
        websiteBuilderUpdateElementStretch(activePageId, deviceView, node.id, {
          id: node.id,
          prevStretch: cloneDeep(prevStretch),
          nextStretch: cloneDeep(nextStretch),
        }),
      );
    } else if (node.type === "container") {
      dispatch(
        websiteBuilderUpdateContainerStretch(activePageId, deviceView, node.id, {
          id: node.id,
          prevStretch: cloneDeep(prevStretch),
          nextStretch: cloneDeep(nextStretch),
        }),
      );
    }
  }

  const { handleUpdateState, handleDebouncedUpdateState } = useWebsiteBuilderDebouncedUpdate({
    state: stretch,
    updatedState: updatedStretch,
    setUpdatedState: setUpdatedStretch,
    dispatchState: dispatchStretch,
    debouncedUpdateTime: WebsiteBuilderDebouncedUpdateTime.MEDIUM,
  });

  function renderStretchSection() {
    return (
      <Toggle
        labelRight={t("Stretch to full width")}
        checked={updatedStretch.enabled}
        onChange={e =>
          handleUpdateState(prev => ({
            ...prev,
            enabled: e.target.checked,
          }))
        }
      />
    );
  }

  function renderMarginSection() {
    if (updatedStretch.enabled) {
      return (
        <WebsiteBuilderRange
          label={t("Margin (px)")}
          min={WEBSITE_BUILDER_SECTION_ENTITY_STRETCH_MIN_MARGIN}
          max={WEBSITE_BUILDER_SECTION_ENTITY_STRETCH_MAX_MARGIN}
          step={1}
          value={updatedStretch.margin}
          setValue={margin =>
            handleDebouncedUpdateState(prev => ({
              ...prev,
              margin,
            }))
          }
        />
      );
    }
  }

  return (
    <WebsiteBuilderPanelContent
      title={t("Stretch settings")}
      closePanel={closeSettings}
      sections={[renderStretchSection(), renderMarginSection()]}
    />
  );
}
